import * as React from "react"
import { AlertTriangle, Play } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Combobox } from "@/components/ui/combobox"
import { csrfToken } from "@/lib/csrf"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type { ScopeOption, TestQueryResult } from "@/types/automation"

const NO_SCOPE = "all"

/**
 * Runs the admin's query against the live roster and reports what a real
 * targeted run would find.
 *
 * This posts with raw fetch() rather than Inertia's router on purpose: testing a
 * query must not navigate, or a half-written automation would be thrown away.
 * AGENTS.md carves out exactly this case for a JSON endpoint.
 */
export function TestQueryPanel({
  rosterQuery,
  scopeOptions,
  onVariables,
}: {
  rosterQuery: string
  scopeOptions: ScopeOption[]
  onVariables: (variables: string[]) => void
}) {
  const [councilId, setCouncilId] = React.useState(NO_SCOPE)
  const [localId, setLocalId] = React.useState(NO_SCOPE)
  const [running, setRunning] = React.useState(false)
  const [result, setResult] = React.useState<TestQueryResult | null>(null)

  const councils = React.useMemo(
    () => scopeOptions.filter((option) => option.level === "district_council"),
    [scopeOptions],
  )

  const locals = React.useMemo(
    () =>
      councilId === NO_SCOPE
        ? []
        : scopeOptions.filter(
            (option) =>
              option.level === "local_union" && String(option.parent_id) === councilId,
          ),
    [scopeOptions, councilId],
  )

  // The Local list belongs to one District Council, so changing the council
  // invalidates whichever Local was picked under the previous one.
  const chooseCouncil = (value: string) => {
    setCouncilId(value)
    setLocalId(NO_SCOPE)
  }

  const run = async () => {
    setRunning(true)
    try {
      const chosen = localId !== NO_SCOPE ? localId : councilId
      const response = await fetch("/admin/automations/test_query", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          roster_query: rosterQuery,
          organization_id: chosen === NO_SCOPE ? null : chosen,
        }),
      })
      const body: TestQueryResult = await response.json()
      setResult(body)
      if (body.ok) onVariables(body.variables)
    } catch (error) {
      setResult(requestFailure(String(error)))
    } finally {
      setRunning(false)
    }
  }

  return (
    <section className="callout space-y-4">
      <div>
        <h2 className="text-base">Test Query</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Runs the query above against the live member roster and reports what it
          finds. Nothing is saved and nothing is sent.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="test-council">Preview for</label>
          <Combobox
            options={[
              { value: NO_SCOPE, label: "All District Councils" },
              ...councils.map((option) => ({
                value: String(option.id),
                label: option.name,
              })),
            ]}
            value={councilId}
            onChange={chooseCouncil}
            searchPlaceholder="Filter District Councils…"
            emptyMessage="No District Council matches."
            triggerLabel="District Council to preview against"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="test-local">Narrow to one Local</label>
          <Combobox
            options={[
              { value: NO_SCOPE, label: "Every Local in this council" },
              ...locals.map((option) => ({
                value: String(option.id),
                label: option.name,
              })),
            ]}
            value={localId}
            onChange={setLocalId}
            disabled={councilId === NO_SCOPE}
            placeholder="Pick a District Council first"
            searchPlaceholder="Filter Locals…"
            emptyMessage="No Local matches."
            triggerLabel="Local Union to preview against"
          />
        </div>
      </div>

      <Button
        type="button"
        variant="secondary"
        onClick={run}
        disabled={running || rosterQuery.trim().length === 0}
      >
        <Play className="h-4 w-4" />
        {running ? "Running…" : "Test Query"}
      </Button>

      {result && !result.ok && (
        <div className="callout callout-danger flex items-start gap-2 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-display" />
          <p className="text-sm text-danger-display">{result.error}</p>
        </div>
      )}

      {result?.ok && <TestQueryResults result={result} />}
    </section>
  )
}

function TestQueryResults({ result }: { result: TestQueryResult }) {
  const columns = result.sample_rows.length
    ? Object.keys(result.sample_rows[0])
    : result.columns

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 xs:grid-cols-3">
        <Stat label="Members matched" value={result.matched_count} emphasis />
        <Stat label="Opted out" value={result.opted_out_count} />
        <Stat label="No mobile number" value={result.no_mobile_count} />
      </div>

      <p className="text-xs text-ink-muted">
        {result.scope_label} · {result.elapsed_ms}ms. Opted-out members and
        members with no mobile number are excluded on every run, whatever the
        query returns.
      </p>

      <div className="space-y-2">
        <h3 className="text-sm">Variables discovered</h3>
        {result.variables.length === 0 ? (
          <p className="text-sm text-ink-muted">
            This query returns nothing beyond the required columns, so there is
            nothing to personalize a message with.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {result.variables.map((variable) => (
              <Badge key={variable} tone="accent">
                {wrap(variable)}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {result.sample_rows.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm">Sample rows</h3>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableHeaderCell key={column}>{column}</TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {result.sample_rows.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column} nowrap>
                      {formatCell(row[column])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-ink-muted">
            Mobile numbers are shown as their last four digits only.
          </p>
        </div>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  emphasis = false,
}: {
  label: string
  value: number | null
  emphasis?: boolean
}) {
  return (
    <div className="rounded-md border border-hairline bg-page px-3 py-2">
      <div
        className={
          emphasis
            ? "text-xl font-medium text-ink-display"
            : "text-xl font-medium text-ink-body"
        }
      >
        {value ?? "—"}
      </div>
      <div className="text-xs text-ink-muted">{label}</div>
    </div>
  )
}

function formatCell(value: unknown) {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "true" : "false"
  return String(value)
}

/** "first_name" -> "{first_name}" without a template literal in JSX. */
function wrap(variable: string) {
  return "{" + variable + "}"
}

function requestFailure(error: string): TestQueryResult {
  return {
    ok: false,
    error,
    matched_count: null,
    opted_out_count: null,
    no_mobile_count: null,
    columns: [],
    variables: [],
    missing_required_columns: [],
    sample_rows: [],
    scope_label: null,
    elapsed_ms: null,
  }
}
